import React from 'react';

const ClientDocuments = ({ documents, submissions, fileSearch, setFileSearch, fileStatus, setFileStatus }) => {
    // Extract CV data from wizard submissions
    const cvData = submissions
        .map(sub => sub.cv_data)
        .filter(cv => cv && Object.keys(cv).length > 0);

    const filteredDocs = documents.filter(doc => {
        let matches = true;
        if (fileStatus) matches = matches && (doc.status || '').toLowerCase() === fileStatus.toLowerCase();
        if (fileSearch.trim()) matches = matches && (doc.title || '').toLowerCase().includes(fileSearch.trim().toLowerCase());
        return matches;
    });

    return (
        <div className="space-y-6">
            {/* CV Information */}
            {cvData.length > 0 && (
                <div className="bg-gray-800 border border-gray-700 rounded-lg shadow-lg overflow-hidden">
                    <div className="bg-gray-900/50 p-4 border-b border-gray-700">
                        <h3 className="text-sm font-bold text-white uppercase tracking-wider">📄 CV & AI Insights</h3>
                    </div>
                    <div className="p-6 space-y-4">
                        {cvData.map((cv, index) => (
                            <div key={index} className="p-4 bg-gray-700/50 rounded border border-gray-600">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                    {cv.fileName && (
                                        <div>
                                            <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">File Name</div>
                                            <div className="text-white font-mono">{cv.fileName}</div>
                                        </div>
                                    )}
                                    {cv.url && (
                                        <div>
                                            <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">CV Link</div>
                                            <a href={cv.url} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 underline font-mono text-sm">
                                                View CV →
                                            </a>
                                        </div>
                                    )}
                                    {cv.publicUrl && !cv.url && (
                                        <div>
                                            <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">CV Link</div>
                                            <a href={cv.publicUrl} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 underline font-mono text-sm">
                                                View CV →
                                            </a>
                                        </div>
                                    )}
                                    {cv.fileSize && (
                                        <div>
                                            <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">File Size</div>
                                            <div className="text-white font-mono">{(cv.fileSize / 1024).toFixed(2)} KB</div>
                                        </div>
                                    )}
                                </div>

                                {/* AI Analysis */}
                                {cv.aiAnalysis && (
                                    <div className="mt-4 p-4 bg-gray-800 rounded border border-gray-600">
                                        <div className="text-xs text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-2">
                                            <span>🤖 AI Analysis</span>
                                        </div>

                                        {cv.aiAnalysis.summary && (
                                            <div className="mb-3">
                                                <div className="text-xs text-gray-400 mb-1">Summary</div>
                                                <div className="text-sm text-gray-300">{cv.aiAnalysis.summary}</div>
                                            </div>
                                        )}

                                        {cv.aiAnalysis.skills && Array.isArray(cv.aiAnalysis.skills) && cv.aiAnalysis.skills.length > 0 && (
                                            <div className="mb-3">
                                                <div className="text-xs text-gray-400 mb-1">Extracted Skills</div>
                                                <div className="flex flex-wrap gap-2">
                                                    {cv.aiAnalysis.skills.map((skill, idx) => (
                                                        <span key={idx} className="px-2 py-1 bg-blue-900/30 text-blue-300 border border-blue-800 rounded text-xs">
                                                            {skill}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {cv.aiAnalysis.experience && (
                                            <div className="mb-3">
                                                <div className="text-xs text-gray-400 mb-1">Experience Highlights</div>
                                                <div className="text-sm text-gray-300">{cv.aiAnalysis.experience}</div>
                                            </div>
                                        )}

                                        {cv.aiAnalysis.education && (
                                            <div className="mb-3">
                                                <div className="text-xs text-gray-400 mb-1">Education Highlights</div>
                                                <div className="text-sm text-gray-300">{cv.aiAnalysis.education}</div>
                                            </div>
                                        )}

                                        {/* Full AI Analysis */}
                                        <details className="mt-3">
                                            <summary className="text-xs text-blue-400 cursor-pointer hover:text-blue-300">View Full AI Analysis</summary>
                                            <pre className="mt-2 p-3 bg-gray-900 rounded text-xs text-gray-300 overflow-auto max-h-96">
                                                {JSON.stringify(cv.aiAnalysis, null, 2)}
                                            </pre>
                                        </details>
                                    </div>
                                )}

                                {/* Full CV Data */}
                                <details className="mt-4">
                                    <summary className="text-xs text-blue-400 cursor-pointer hover:text-blue-300">View All CV Data</summary>
                                    <pre className="mt-2 p-3 bg-gray-900 rounded text-xs text-gray-300 overflow-auto">
                                        {JSON.stringify(cv, null, 2)}
                                    </pre>
                                </details>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Documents List */}
            <div className="bg-gray-800 border border-gray-700 rounded-lg shadow-lg overflow-hidden">
                <div className="bg-gray-900/50 p-4 border-b border-gray-700">
                    <h3 className="text-sm font-bold text-white uppercase tracking-wider">📁 Documents</h3>
                </div>
                <div className="p-6">
                    {/* Filters */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <input
                            className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="🔍 Search documents"
                            value={fileSearch}
                            onChange={(e) => setFileSearch(e.target.value)}
                        />
                        <select
                            className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            value={fileStatus}
                            onChange={(e) => setFileStatus(e.target.value)}
                        >
                            <option value="">All statuses</option>
                            <option value="pending">Pending</option>
                            <option value="approved">Approved</option>
                            <option value="rejected">Rejected</option>
                        </select>
                    </div>

                    {/* Documents Table */}
                    <div className="space-y-2">
                        {filteredDocs.map(doc => (
                            <div key={doc.id} className="p-3 bg-gray-700/50 rounded border border-gray-600 flex items-center justify-between hover:bg-gray-700 transition-colors">
                                <div className="flex-1">
                                    <div className="text-sm font-mono text-white">{doc.title || 'Untitled'}</div>
                                    <div className="text-xs text-gray-400 mt-1">
                                        {doc.created_at ? new Date(doc.created_at).toLocaleDateString() : '-'}
                                    </div>
                                </div>
                                <span className={`px-2 py-1 rounded text-xs font-bold uppercase ${doc.status === 'approved' ? 'bg-green-900/30 text-green-300 border border-green-800' :
                                        doc.status === 'rejected' ? 'bg-red-900/30 text-red-300 border border-red-800' :
                                            'bg-yellow-900/30 text-yellow-300 border border-yellow-800'
                                    }`}>
                                    {doc.status || 'pending'}
                                </span>
                            </div>
                        ))}
                        {filteredDocs.length === 0 && (
                            <div className="text-sm text-gray-400 text-center py-12">
                                {fileSearch || fileStatus ? 'No documents match your filters' : 'No documents available'}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ClientDocuments;
